// LiveTerminal · faux order-routing console that streams realistic
// log lines underneath the LD4 flow diagram. Pure visual storytelling
// — communicates "we run actual infrastructure" in a way marketing
// copy never can. No real backend traffic, but the line generator
// uses real symbols + plausible latencies so it reads as authentic.
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TerminalSquare, Wifi, Cpu } from "lucide-react";

const SYMBOLS = [
  "EURUSD", "GBPUSD", "USDJPY", "AUDUSD", "USDCAD", "EURGBP", "EURJPY", "GBPJPY",
  "XAUUSD", "XAGUSD", "BTCUSD", "ETHUSD", "USOIL", "UKOIL",
  "NAS100", "SPX500", "GER40", "UK100", "US30",
];
const LP_VENUES = ["JPM", "GS", "MS", "DB", "BARC", "UBS", "CITI", "HSBC", "RBS", "BNP", "SOC", "ANZ"];

// Per-symbol approximate price · used so the printed quotes look real.
const QUOTE = {
  EURUSD: 1.0842, GBPUSD: 1.2641, USDJPY: 149.32, AUDUSD: 0.6502, USDCAD: 1.3725,
  EURGBP: 0.8576, EURJPY: 161.85, GBPJPY: 188.71,
  XAUUSD: 2034.5, XAGUSD: 23.18, BTCUSD: 68420, ETHUSD: 3582,
  USOIL: 78.42, UKOIL: 82.15,
  NAS100: 17893.4, SPX500: 5012.7, GER40: 18421, UK100: 7842, US30: 38924,
};

function fmt(sym, v) {
  if (sym.startsWith("BTC") || sym === "ETHUSD" || sym.endsWith("100") || sym.endsWith("40") || sym.endsWith("30") || sym === "SPX500") {
    return Math.round(v).toLocaleString();
  }
  if (sym.endsWith("JPY")) return v.toFixed(3);
  if (sym === "USOIL" || sym === "UKOIL" || sym === "XAGUSD") return v.toFixed(2);
  return v.toFixed(5);
}

// Generate a single realistic log line · 90% ROUTE / 8% FILL / 2% PARTIAL.
function genLine(seq) {
  const sym = SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)];
  const lp = LP_VENUES[Math.floor(Math.random() * LP_VENUES.length)];
  const base = QUOTE[sym] || 1;
  const px = base * (1 + (Math.random() - 0.5) * 0.0008);
  const lat = (0.3 + Math.random() * 1.4).toFixed(1);
  const size = [50, 100, 200, 500, 1000][Math.floor(Math.random() * 5)];
  const r = Math.random();
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  const ss = String(now.getSeconds()).padStart(2, "0");
  const ms = String(now.getMilliseconds()).padStart(3, "0");
  const ts = `${hh}:${mm}:${ss}.${ms}`;
  let kind, color, body;
  if (r < 0.08) {
    kind = "FILL";
    color = "#54d97c";
    body = `${sym.padEnd(6)} ${String(size).padStart(4)}k @ ${fmt(sym, px)} · ${lat}ms`;
  } else if (r < 0.10) {
    kind = "PARTIAL";
    color = "#f5b14a";
    body = `${sym.padEnd(6)} → LD4-A1 → ${lp.padEnd(4)} · re-route ${lp === "JPM" ? "GS" : "JPM"}`;
  } else {
    kind = "ROUTE";
    color = "#7be0ff";
    body = `${sym.padEnd(6)} → LD4-A1 → ${lp.padEnd(4)} · ack ${lat}ms`;
  }
  return { id: seq, ts, kind, color, body };
}

export default function LiveTerminal() {
  const [lines, setLines] = useState(() => Array.from({ length: 7 }, (_, i) => genLine(i)));
  const [counter, setCounter] = useState({ routed: 847232, filled: 78421 });
  const seqRef = useRef(7);
  const bodyRef = useRef(null);

  // Stream a new log line every 320-520ms · keeps the window feeling
  // live without overwhelming the eye.
  useEffect(() => {
    let t;
    const tick = () => {
      const ln = genLine(seqRef.current++);
      setLines((prev) => [...prev.slice(-9), ln]);
      setCounter((c) => ({
        routed: c.routed + 1,
        filled: c.filled + (ln.kind === "FILL" ? 1 : 0),
      }));
      t = setTimeout(tick, 320 + Math.random() * 200);
    };
    t = setTimeout(tick, 380);
    return () => clearTimeout(t);
  }, []);

  // Auto-scroll to bottom on new line · respects user scroll position
  // (we always force to bottom because the panel is decorative).
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [lines]);

  return (
    <div
      className="relative rounded-2xl overflow-hidden h-full flex flex-col"
      style={{
        borderColor: "var(--term-border)",
        borderWidth: "1px",
        borderStyle: "solid",
        background: "var(--term-bg)",
        boxShadow: "var(--term-shadow)",
      }}
      data-testid="liquidity-terminal"
    >
      {/* Window chrome · macOS-style traffic lights + title bar */}
      <div
        className="flex items-center gap-2.5 px-3.5 py-2 border-b"
        style={{
          borderColor: "var(--term-border-inner)",
          background: "var(--term-chrome)",
        }}
      >
        <div className="flex gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: "#ff5f57" }} />
          <span className="w-2 h-2 rounded-full" style={{ background: "#febc2e" }} />
          <span className="w-2 h-2 rounded-full" style={{ background: "#28c840" }} />
        </div>
        <div className="flex items-center gap-1.5 ml-1 font-mono text-[9.5px] min-w-0" style={{ color: "var(--term-text-dim)" }}>
          <TerminalSquare size={11} style={{ color: "var(--brand)" }} className="shrink-0" />
          <span className="hidden sm:inline truncate">order-router@ld4-a1:~$ tail -f /var/log/routing.stream</span>
          <span className="sm:hidden truncate">routing.stream</span>
        </div>
        <div className="ml-auto inline-flex items-center gap-1 font-mono text-[12px] shrink-0" style={{ color: "var(--term-text-dim)" }}>
          <motion.span
            className="inline-block w-1.5 h-1.5 rounded-full"
            style={{ background: "var(--term-kw-fill)", boxShadow: "0 0 5px var(--term-kw-fill)" }}
            animate={{ opacity: [1, 0.25, 1] }}
            transition={{ duration: 1.2, repeat: Infinity }}
          />
          <span style={{ color: "var(--term-kw-fill)" }}>LIVE</span>
        </div>
      </div>

      {/* Body · streaming log lines · flex-1 fills the locked parent
          height so the terminal never grows as new lines stream in.
          overflow-hidden + autoscroll-to-bottom keeps the latest
          entries visible. */}
      <div
        ref={bodyRef}
        className="flex-1 min-h-0 overflow-hidden px-3.5 py-2.5 font-mono text-[12px] leading-[1.55]"
        style={{ color: "var(--term-text)" }}
      >
        <AnimatePresence initial={false}>
          {lines.map((ln) => (
            <motion.div
              key={ln.id}
              initial={{ opacity: 0, x: -4 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="flex items-start gap-2 whitespace-pre"
            >
              <span className="shrink-0 tabular-nums" style={{ color: "var(--term-text-dim)" }}>[{ln.ts}]</span>
              <span
                className="shrink-0 font-semibold tabular-nums"
                style={{
                  color: `var(--term-kw-${ln.kind.toLowerCase()})`,
                  width: "7ch",
                }}
              >
                {ln.kind}
              </span>
              <span className="tabular-nums truncate" style={{ color: "var(--term-text-muted)" }}>{ln.body}</span>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Footer · live counters */}
      <div
        className="grid grid-cols-3 px-3.5 py-2 border-t font-mono text-[9.5px] sm:text-[12px] uppercase tracking-[0.08em] sm:tracking-[0.18em]"
        style={{
          borderColor: "var(--term-border-inner)",
          background: "var(--term-footer)",
          color: "var(--term-text-dim)",
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Wifi size={11} style={{ color: "var(--brand)" }} className="shrink-0" />
          <span className="truncate">routed <span style={{ color: "var(--term-text-strong)" }} className="tabular-nums">{counter.routed.toLocaleString()}</span></span>
        </div>
        <div className="flex items-center gap-2 min-w-0 justify-center">
          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "var(--term-kw-fill)" }} />
          <span className="truncate">filled <span style={{ color: "var(--term-text-strong)" }} className="tabular-nums">{counter.filled.toLocaleString()}</span></span>
        </div>
        <div className="flex items-center gap-2 min-w-0 justify-end">
          <Cpu size={11} style={{ color: "var(--brand)" }} className="shrink-0" />
          <span className="truncate">cpu <span style={{ color: "var(--term-text-strong)" }} className="tabular-nums">34%</span></span>
        </div>
      </div>
    </div>
  );
}
